import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Youtube, Twitter, Download, Calendar } from 'lucide-react';
import { siteConfig } from '../config/siteConfig';

const LOGO = "https://customer-assets.emergentagent.com/job_e8d411ed-442e-4bb2-8983-8c3ef5c754a7/artifacts/0cxz66vb_IMG_20260403_184615.jpg";

export default function Footer() {
  return (
    <footer className="bg-[#1A1A1A] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">

          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-2.5 inline-block mb-4">
              <img src={LOGO} alt={siteConfig.name} className="h-12 w-auto object-contain" />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              {siteConfig.name} provides a safe, joyful, and inspiring environment where young children learn through play, creativity, exploration, and meaningful experiences.
            </p>
            <div className="flex gap-2.5 mb-5">
              {[
                { Icon: Facebook, href: siteConfig.socials.facebook, label: "Facebook" },
                { Icon: Instagram, href: siteConfig.socials.instagram, label: "Instagram" },
                { Icon: Youtube, href: siteConfig.socials.youtube, label: "YouTube" },
                { Icon: Twitter, href: siteConfig.socials.twitter, label: "Twitter" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="w-9 h-9 bg-white/10 hover:bg-[#C8161D] rounded-full flex items-center justify-center transition-colors duration-200"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <a 
                href={siteConfig.brochureUrl}
                download
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#9DC41E] hover:text-[#86A917] transition-colors"
              >
                <Download size={14} /> Download Brochure
              </a>
              <Link 
                to="/contact" 
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#C8161D] hover:text-[#A61217] transition-colors"
              >
                <Calendar size={14} /> Book a School Tour
              </Link>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="font-nunito font-bold text-base mb-4 text-[#C8161D]">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "About Us" },
                { to: "/admissions", label: "Admissions" },
                { to: "/gallery", label: "Gallery" },
                { to: "/parents-corner", label: "Parents Corner" },
                { to: "/careers", label: "Careers" },
                { to: "/contact", label: "Contact Us" },
                { to: "/franchise", label: "Franchise" },
              ].map(link => (
                <li key={link.label}>
                  <Link to={link.to} className="text-gray-400 text-sm hover:text-[#9DC41E] transition-colors flex items-center gap-1.5 group">
                    <span className="w-1.5 h-1.5 bg-[#9DC41E] rounded-full flex-shrink-0 group-hover:scale-125 transition-transform" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Learning */}
          <div>
            <h4 className="font-nunito font-bold text-base mb-4 text-[#9DC41E]">Learning</h4>
            <ul className="space-y-2">
              {[
                { to: "/programs#playgroup", label: "Playgroup (1.5-2.5 yrs)" },
                { to: "/programs#nursery", label: "Nursery (2.5-3.5 yrs)" },
                { to: "/programs#lkg", label: "LKG (3.5-4.5 yrs)" },
                { to: "/programs#ukg", label: "UKG (4.5-5.5 yrs)" },
                { to: "/academics", label: "Academics" },
                { to: "/activities", label: "Activities" },
                { to: "/facilities", label: "Facilities" },
              ].map(link => (
                <li key={link.label}>
                  <Link to={link.to} className="text-gray-400 text-sm hover:text-[#9DC41E] transition-colors flex items-center gap-1.5 group">
                    <span className="w-1.5 h-1.5 bg-[#C8161D] rounded-full flex-shrink-0 group-hover:scale-125 transition-transform" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Parent Services */}
          <div>
            <h4 className="font-nunito font-bold text-base mb-4 text-sky-300">Parent Services</h4>
            <ul className="space-y-2">
              {[
                { to: "/admissions", label: "Online Admission" },
                { to: "/parents-corner#communication", label: "Communication" },
                { to: "/parents-corner#fee-payment", label: "Fee Payment" },
                { to: "/parents-corner#mobile-app", label: "School Mobile App" },
                { to: "/parents-corner#testimonials", label: "Testimonials" },
                { to: "/parents-corner#reviews", label: "Google Reviews" },
                { to: siteConfig.brochureUrl, label: "Download Brochure", download: true },
              ].map((link, idx) => (
                <li key={idx}>
                  {link.download ? (
                    <a href={link.to} download className="text-gray-400 text-sm hover:text-[#9DC41E] transition-colors flex items-center gap-1.5 group">
                      <span className="w-1.5 h-1.5 bg-sky-300 rounded-full flex-shrink-0 group-hover:scale-125 transition-transform" />
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.to} className="text-gray-400 text-sm hover:text-[#9DC41E] transition-colors flex items-center gap-1.5 group">
                      <span className="w-1.5 h-1.5 bg-sky-300 rounded-full flex-shrink-0 group-hover:scale-125 transition-transform" />
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Contact */}
          <div>
            <h4 className="font-nunito font-bold text-base mb-4 text-yellow-200">Contact Us</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2.5">
                <MapPin size={16} className="text-[#9DC41E] flex-shrink-0 mt-0.5" />
                <p className="text-gray-400 text-sm leading-relaxed">
                  {siteConfig.address}
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone size={16} className="text-[#9DC41E] flex-shrink-0" />
                <a href={`tel:${siteConfig.phoneRaw}`} className="text-gray-400 text-sm hover:text-white transition-colors">
                  {siteConfig.phone}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail size={16} className="text-[#9DC41E] flex-shrink-0" />
                <a href={`mailto:${siteConfig.email}`} className="text-gray-400 text-sm hover:text-white transition-colors break-all">
                  {siteConfig.email}
                </a>
              </div>
              <div className="p-2.5 bg-white/5 rounded-xl mt-1">
                <p className="text-gray-400 text-xs">
                  <span className="text-[#9DC41E] font-semibold">Hours:</span> {siteConfig.hours}
                </p>
              </div>
              <div>
                <a
                  href={siteConfig.googleMapsDirections}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 transition-colors font-medium mt-1"
                >
                  <MapPin size={12} /> Get Directions on Map
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm text-center">
            © {new Date().getFullYear()} {siteConfig.name}. All Rights Reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/contact" className="text-gray-500 text-sm hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/contact" className="text-gray-500 text-sm hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
